"use strict";

import Immutable, { fromJS, List, Map } from "immutable";
import { nanoid } from "nanoid";
import _ from "lodash";

import AppConstants from "../../src/constants/AppConstants.js";
import NameSlugFormatter from "../utils/NameSlugFormatter.js";
import ModelFactory from "../models/ModelFactory";
import { offsetElement } from "../utils/ElementUtils.js";
import ElementContainer from "./ElementContainer";
import { ELEMENTS, PARENT_ID, STATES } from "@/constants/EntityConstants.js";
import { sortByReadingOrder } from "@/utils/ReadingOrderUtils";
import IDUtils from "@/utils/IDUtils.js";
import DataTypes from "@/constants/DataTypes.js";

const { ID_LENGTH } = AppConstants;

/**
 * @typedef {Object} ElementInfo
 * @property {string} id - Selection path to the element within the block.
 * @property {string} type - The type of the element.
 * @property {string} blockId - The ID of the block.
 * @property {number} blockIndex - The index of the block.
 * @property {boolean=} contained - Indicates if the element is part of a group.
 * @property {string=} containedBy - Selection path id to containing element.
 * @property {boolean=} edit - Indicates the element is being edited.
 * @property {number} index - The index of the element within its parent container.
 * @property {boolean} lock - Indicates if the element is locked for editing.
 * @property {number} stateId - The ID of the state, -1 if not in a state.
 */

class Block {
  constructor(/** Immutable **/ data, /** BlockContainer **/ parent) {
    this.data = data;
    this.parent = parent;
  }

  addState(stateData, config) {
    var insertAt = _.get(config, "insertAt", false);

    var states = this.getStates();
    if (states === false) {
      states = Immutable.List();
    }

    stateData.name = _.get(stateData, "name", "View " + (states.size + 1));

    var nameAndSlug = NameSlugFormatter.format(stateData.name, states);

    stateData.name = nameAndSlug.name;
    stateData.slug = nameAndSlug.slug;

    var state = Immutable.fromJS(stateData);

    if (insertAt && insertAt >= 0) {
      states = states.splice(insertAt, 0, state);
    } else {
      states = states.push(state);
    }

    this.data = this.data.set("states", states);

    var statesIndex = this.getStateElementIndex();
    if (statesIndex == -1) {
      this._createStatesElement();
    }

    return { state: state, index: insertAt ? insertAt : states.size - 1 };
  }

  removeState(stateIndex) {
    var states = this.getStates();
    var state = this.getState(stateIndex);

    var result = {
      index: -1,
    };

    if (states && state) {
      states = states.delete(stateIndex);

      result.index = stateIndex;
      result.state = state;

      this.data = this.data.set("states", states);

      //remove states element placeholder
      if (states.size == 0) {
        this._removeStatesElement();
      }
    }

    return result;
  }

  updateStatePosition(/** Array **/ path, index) {
    var state = this.getState(path);
    var updatedPath = path;

    if (state) {
      var stateIndex = path[2];
      var states = this.getStates();

      states = states.splice(stateIndex, 1);

      states = states.splice(index, 0, state);

      this.data = this.data.set("states", states);

      updatedPath = this._generateStatePath(index);
    }

    return updatedPath;
  }

  insertStateAt(/** Immutable **/ state, index) {
    var states = this.getStates();
    if (states === false) {
      states = Immutable.List();
    }

    return this._insertState(states, state, index);
  }

  _insertState(/** Immutable.List **/ states, /** Immutable **/ state, index) {
    if (index && index >= 0) {
      states = states.splice(index, 0, state);
    } else {
      states = states.push(state);
      index = states.size - 1;
    }

    this.data = this.data.set("states", states);

    var statesIndex = this.getStateElementIndex();
    if (statesIndex == -1) {
      this._createStatesElement();
    }

    return this._generateStatePath(index);
  }

  getState(stateIndexOrPath) {
    var index = stateIndexOrPath;
    if (_.isArray(stateIndexOrPath)) {
      if (stateIndexOrPath.length == 3) {
        index = parseInt(stateIndexOrPath[2]);
      }
    }

    return this.data.getIn(["states", index], false);
  }

  getStates() {
    return this.data.getIn([STATES], false);
  }

  getStateElements(stateIndexOrPath) {
    var state = this.getState(stateIndexOrPath);
    var elements = null;

    return state ? state.get(ELEMENTS) : null;
  }

  /**
   * Returns the index of the states placeholder element
   * @returns {*}
   */
  getStateElementIndex() {
    return this.data.get(ELEMENTS).findIndex((elem) => elem.get("type") === STATES);
  }

  /**
   * Takes a state returned from getState and provides the path
   * @param element
   * @return array where each element is part of the path to the specified state
   */
  getStatePath(/** Immutable **/ state) {
    var path = null;
    var states = this.getStates();
    if (states) {
      var index = states.indexOf(state);
      if (index != -1) {
        path = [this.id, STATES, index];
      }
    }

    return path;
  }

  /**
   * Adds element somewhere within a block, may alter to fit destination requirements.
   * @param {Array<Object>} elements
   * @param {string=} stateIndex
   * @param {{ insertAt: number, containedBy: string, isNew?: boolean }=} config
   * @returns {Array<Array<string>>} - Paths to the newly added elements
   */
  addElements(elements, stateIndex, config) {
    let { container, path } = this._getContainerAndPath(stateIndex);

    let immElements = [];
    let elementPaths = [];
    if (elements && container) {
      // add to elementContainer
      if (config?.containedBy) {
        let elementContainer = this.getElementContainerEntity(config.containedBy);

        elementPaths.push(
          ...elementContainer
            .contain(
              elements.map((element) => fromJS(element)),
              undefined,
              !config.isNew
            )
            .map((id) => id.split("."))
        );

        elementContainer.applyToParent();
      } else {
        // normal add
        let insertAt = config?.insertAt ?? container.size;

        var elemIndex = insertAt;
        immElements = elements.map((elem) => {
          var elemPath = _.concat(path, elemIndex);
          elemPath.unshift(this.id);
          elementPaths.push(elemPath);

          elemIndex++;

          // remove pId in all cases if set
          return (Map.isMap(elem) ? elem : Immutable.fromJS(elem)).delete(PARENT_ID);
        });

        container = container.splice(insertAt, 0, ...immElements);

        this.data = this.data.setIn(path, container);
      }
    }

    return elementPaths;
  }

  /**
   * Inserts elements as is
   * @param {Array<Object>} elements An Array of Immutable Maps that represent elements
   * @param {number} index if not state index provide it is the index in the block,
   *              if a state index is given then it is the index in the state
   * @param {(number | null)=} stateIndex the state the elements should be added to
   * @param {(boolean | import("@/entities/ElementContainer.js").Target)=} contain the state the elements should be added to
   * @param {boolean=} reverse - reverse the insert order. Used with contained elements.
   * @returns {Array<Array<string>>} - Paths to the newly added elements
   */
  insertElements(elements, index, stateIndex, contain, reverse) {
    let { container, path } = this._getContainerAndPath(stateIndex);

    let elementPaths = [];

    if (elements && container) {
      if (contain) {
        const containPath =
          typeof contain === "object"
            ? this.findElementContainerEntityById(contain.containedBy)
            : [this.id, ...path, index];

        let elementContainer = this.getElementContainerEntity(containPath);

        let immutableElements = elements.map((element) => fromJS(element));
        if (reverse) {
          immutableElements = immutableElements.toReversed();
        }
        const insertedElements =
          typeof contain === "object"
            ? elementContainer.insertElements(immutableElements, contain)
            : elementContainer.contain(immutableElements);

        elementPaths.push(...insertedElements);

        elementContainer.applyToParent();
      } else {
        let insertAt = _.isNil(index) ? container.size : index;
        var elemIndex = insertAt;
        elements.forEach((element) => {
          var elemPath = _.concat(path, elemIndex);
          elemPath.unshift(this.id);
          elementPaths.push(elemPath);

          elemIndex++;
        });

        container = container.splice(insertAt, 0, ...elements);

        this.data = this.data.setIn(path, container);
      }
    }

    return elementPaths;
  }

  _pathsShareContainer(paths) {
    const firstContainer = this.getContainingParentEntity(paths[0]);

    return firstContainer && paths.length > 1
      ? paths.every((path) => firstContainer.contains(path))
      : false;
  }

  /**
   * Removes elements from the block, returns results with path at deletion and data if found.
   * @param {Array<Array<string>>} elementPaths - A list of element paths as arrays.
   * @returns {Array<{ path: Array<string>, element: Map }>}
   */
  removeElements(elementPaths) {
    //only get paths that match the format of block or state elements
    const formattedPaths = elementPaths.filter(this._isValidItemPath);
    let paths = this._sortElementPaths(formattedPaths);
    const sharedContainer = this._pathsShareContainer(paths);

    if (paths.length && sharedContainer) {
      const elementContainer = this.getContainingParentEntity(paths[0]);
      paths = elementContainer.reorderPaths(paths);
    }

    const elements = paths.map((path) => {
      //remove the block id from path
      const bPath = path.slice(1);

      let element = this.data.getIn(bPath);
      if (element) {
        if (element.get(PARENT_ID)) {
          // release from container if contained
          const container = this.getContainingParentEntity(path);

          element = container.releaseElement(path, true);
          container.applyToParent();
        } else {
          // regular removal
          this.data = this.data.deleteIn(bPath);
        }
      }

      return { path, element };
    });

    return elements;
  }

  /**
   * Swaps element positions in a block
   * @param {Array<Array<string>>} elementPaths
   * @param {number} index
   * @param {(number | string)=} stateIndex
   * @param {boolean | (import("@/entities/ElementContainer.js").Target)=} contain - contain elements at block and state position. Uses index and state to point to container if contained.
   * @param {boolean=} reverse - Reverse the insertion order. Likely some in/out group operation.
   * @returns {Array<{old: string, new?: string}>}
   */
  updateElementPositions(elementPaths, index, stateIndex, contain, reverse) {
    stateIndex = !_.isNil(stateIndex) ? parseInt(stateIndex) : stateIndex;

    var finalIndex = index;

    var removed = this.removeElements(elementPaths);

    var containerPath = [ELEMENTS];
    if (!_.isNil(stateIndex)) {
      containerPath.unshift(stateIndex);
      containerPath.unshift(STATES);
    }
    containerPath.unshift(this.id);

    var updates = [];
    var elements = [];

    removed.forEach((data) => {
      var element = data.element;
      var path = data.path;

      if (!_.isNil(index) && _.isEqual(_.dropRight(path), containerPath)) {
        if (_.last(path) < index) {
          finalIndex--;
        }
      }

      elements.push(element);
    });

    elements.reverse();

    var updated = this.insertElements(elements, finalIndex, stateIndex, contain, reverse);

    removed.forEach(function (data, idx) {
      var result = { old: data.path };
      if (updated.length > idx) {
        result.new = updated[idx];
      }
      updates.push(result);
    });

    return updates;
  }

  /**
   * Reorders elements in this block (including every state) so they appear
   * in top-to-bottom, left-to-right reading order for the given layout.
   * Recurses into groups, reordering contained elements as well.
   *
   * Mutates this.data. Caller must invoke applyToParent() to persist.
   *
   * @param {"desktop" | "mobile"} layout
   */
  sortElementsByReadingOrder(layout) {
    const topElements = this.data.get(ELEMENTS);
    if (topElements) {
      this.data = this.data.set(ELEMENTS, sortByReadingOrder(topElements, layout));
    }
    this._sortNestedGroups([ELEMENTS], layout);

    const states = this.getStates();
    if (states) {
      states.forEach((_state, stateIndex) => {
        const statePath = [STATES, stateIndex, ELEMENTS];
        const stateElements = this.data.getIn(statePath);
        if (stateElements) {
          this.data = this.data.setIn(statePath, sortByReadingOrder(stateElements, layout));
        }
        this._sortNestedGroups(statePath, layout);
      });
    }
  }

  /**
   * Walks the element list at containerPath and, for any element that is
   * itself a group (has its own `elements` map), recursively applies the
   * reading-order sort via an ElementContainer entity.
   *
   * @param {Array<string | number>} containerPath - path to a List of elements
   * @param {"desktop" | "mobile"} layout
   */
  _sortNestedGroups(containerPath, layout) {
    const list = this.data.getIn(containerPath);
    if (!list) return;
    list.forEach((element, index) => {
      if (element && element.get(ELEMENTS)) {
        const fullPath = [...containerPath, index];
        const elementContainer = new ElementContainer(element, this, [this.id, ...fullPath]);
        elementContainer.sortByReadingOrder(layout);
        this.data = this.data.setIn(fullPath, elementContainer.data);
      }
    });
  }

  moveElementsToBlock(/** Array **/ elementPaths, /** Block **/ targetBlock) {
    var removed = this.removeElements(elementPaths);
    var elements = _.map(removed, "element");

    elements.reverse();
    targetBlock.insertElements(elements);

    return targetBlock;
  }

  markAsSelected(/** Array **/ elementPaths, /** optional.Array **/ statePath) {
    var paths = _.filter(elementPaths, this._isValidItemPath);
    _.forEach(paths, _.bind(this._markSelected, this));

    if (statePath) {
      this._markSelected(statePath);
    }
  }

  _markSelected(path) {
    var bPath = _.drop(path, 1); //remove block id from path;
    if (this.data.hasIn(bPath)) {
      this.data = this.data.mergeIn(bPath, { selected: 1 });
    }
  }

  clearMarked() {
    var selected = this.findSelected();

    _.forEach(selected.elements, _.bind(this._clearMarked, this));

    if (selected.state) {
      this._clearMarked(selected.state);
    }
  }

  _clearMarked(path) {
    var bPath = _.drop(path, 1);
    bPath.push("selected");
    this.data = this.data.deleteIn(bPath);
  }

  /**
   * Collects selected item paths
   * @returns {{
   *  state: Array<string> | null,
   *  elements: Array<Array<string>>
   * }}
   */
  findSelected() {
    var paths = {
      state: null,
      elements: [],
    };

    var elements = this.getElements();
    if (elements) {
      paths.elements = this._getSelectedElementPaths(elements, [this.id, ELEMENTS]);
    }

    var states = this.getStates();

    if (states) {
      states.forEach(function (state, index) {
        var statePath = [this.id, STATES, index];
        if (state.has("selected")) {
          paths.state = statePath;
        }

        var elements = state.get(ELEMENTS);
        paths.elements = paths.elements.concat(
          this._getSelectedElementPaths(elements, _.concat(statePath, ELEMENTS))
        );
      }, this);
    }

    return paths;
  }

  determineSelectedElementsInRange(
    /** Array **/ selectedElementPaths,
    /** Array **/ clickedElementPath,
    /** Array **/ lastElementPath,
    /** Array **/ selectedStatePath
  ) {
    if (
      _.isEmpty(selectedElementPaths) ||
      selectedElementPaths.length < 1 ||
      _.isEmpty(clickedElementPath) ||
      _.isEmpty(lastElementPath)
    ) {
      return selectedElementPaths;
    }

    var selectedPaths = _.map(selectedElementPaths, this._normalizeItemPath);
    var clickedPath = this._normalizeItemPath(clickedElementPath);
    var lastPath = this._normalizeItemPath(lastElementPath);

    var stateIndex = null;
    if (!_.isEmpty(selectedStatePath)) {
      stateIndex = _.last(this._normalizeItemPath(selectedStatePath));
    }

    var statesIndex = this.getStateElementIndex();
    var elements = this.getElements();

    var elementSelection = [];

    elements.forEach(function (elem, index) {
      var path = null;

      if (statesIndex >= 0 && index == statesIndex) {
        if (stateIndex >= 0) {
          var stateElements = this.getStateElements(stateIndex);
          if (stateElements) {
            stateElements.forEach(function (stateElem, elemIndex) {
              path = [this.id, STATES, stateIndex, ELEMENTS, elemIndex];
              this._setElementSelection(elementSelection, path, selectedPaths);
            }, this);
          }
        }
      } else {
        path = [this.id, ELEMENTS, index];
        this._setElementSelection(elementSelection, path, selectedPaths);
      }
    }, this);

    var clickedIndex = _.findIndex(elementSelection, function (s) {
      return _.isEqual(clickedPath, s.path);
    });
    var lastIndex = _.findIndex(elementSelection, function (s) {
      return _.isEqual(lastPath, s.path);
    });

    var startIndex = Math.min(clickedIndex, lastIndex);
    var endIndex = Math.max(clickedIndex, lastIndex);

    var updatedSelectedPaths = selectedPaths;

    if (startIndex >= 0 && endIndex >= 0) {
      for (var i = startIndex; i <= endIndex; i++) {
        elementSelection[i].selected = true;
      }

      updatedSelectedPaths = _.map(
        _.filter(elementSelection, { selected: true }),
        _.unary(_.partialRight(_.get, "path"))
      );
    }

    return updatedSelectedPaths;
  }

  /**
   * Used by determineSelectedElementsInRange
   * @param selection
   * @param elementPath
   * @param selectedPaths
   * @param clickedPath
   * @private
   */
  _setElementSelection(selection, elementPath, selectedPaths) {
    var selected = false;

    if (
      _.findIndex(selectedPaths, function (p) {
        return _.isEqual(p, elementPath);
      }) >= 0
    ) {
      selected = true;
    }

    selection.push({ path: elementPath, selected: selected });
  }

  /**
   * Collects paths to selected elements
   * @param {List<Map>} elements
   * @param {Array<string | number>} basePath
   * @returns {Array<Array<string>>}
   */
  _getSelectedElementPaths(elements, basePath) {
    var paths = [];

    elements.forEach((elem, index) => {
      if (elem.has("selected")) {
        paths.push(basePath.concat(index));
      }
      // handle contained element paths
      else if (elem.get("type") === DataTypes.GROUP) {
        elem.get(ELEMENTS).forEach((containedElem, id) => {
          if (containedElem.has("selected")) {
            paths.push(basePath.concat(index, ELEMENTS, id));
          }
        });
      }
    });

    return paths;
  }

  _sortElementPaths(elementPaths) {
    var paths = _.map(elementPaths, this._normalizeItemPath);

    var statesIndex = this.getStateElementIndex();
    var comparator = this._pathComparator.bind(this, statesIndex);

    paths.sort(comparator);

    return paths;
  }

  _pathComparator(statesIndex, /** Array **/ pathA, /** Array **/ pathB) {
    if (pathA.length == pathB.length) {
      for (var i = 0; i < pathA.length; i++) {
        if (pathA[i] != pathB[i]) {
          if (pathA[i] < pathB[i]) {
            return 1;
          } else if (pathA[i] > pathB[i]) {
            return -1;
          }
        }
      }
    } else if (pathA.length < pathB.length) {
      if (statesIndex >= 0) {
        if (_.last(pathA) > statesIndex) {
          return -1;
        } else {
          return 1;
        }
      } else {
        return 1;
      }
    } else if (pathA.length > pathB.length) {
      if (statesIndex >= 0) {
        if (_.last(pathB) > statesIndex) {
          return 1;
        } else {
          return -1;
        }
      } else {
        return -1;
      }
    }

    return 0;
  }

  _normalizeItemPath(path) {
    if (path.length >= 3) {
      path[2] = parseInt(path[2]);
      if (path.length == 5) {
        const parseResult = parseInt(path[4]);
        // parse if number and preserve if ID
        path[4] = !Number.isNaN(parseResult) && path[4].length < ID_LENGTH ? parseResult : path[4];
      }
    }

    return path;
  }

  /**
   * Returns true if the path array should point to a valid location of an element.
   * @param {Array<string>} path
   * @returns {boolean}
   */
  _isValidItemPath(/** Array **/ path) {
    return (
      IDUtils.isRegularElementPath(path) ||
      (path.length == 5 &&
        (IDUtils.isStateElementPath(path) || IDUtils.isRegularContainedElementPath(path))) ||
      (path.length === 7 && IDUtils.isStateContainedElementPath(path))
    );
  }

  duplicateElementAt(elementPath, options = {}, shiftAmount = 10) {
    let path = _.cloneDeep(elementPath);
    //popup off the block id if it is set
    if (path.length == 3 || path.length == 5) {
      path.shift();
    }

    const elementId = parseInt(_.last(elementPath));
    const elementIdIsIndex = !Number.isNaN(elementId);

    const elementIndex = elementIdIsIndex ? parseInt(_.last(elementPath)) : _.last(elementPath);
    const stateIndex = path.length == 4 ? parseInt(path[1]) : null;

    const element = this.getElement(elementPath);

    let duplicatedElementPath = null;

    if (element) {
      let newElement = offsetElement(element, shiftAmount);

      const containerId = element.get("pId");
      // if element is in a group, handle as group element
      if (containerId) {
        const containerPath = this.findElementContainerEntityById(containerId);
        const elementContainer = this.getElementContainerEntity(containerPath);
        const prevElementIndex = elementContainer._getElementIndex(elementPath.at(-1));

        duplicatedElementPath = elementContainer.contain(
          [newElement],
          prevElementIndex + 1,
          false
        )[0];
      } else {
        Object.assign(newElement, options);

        const elementPaths = this.addElements([newElement], stateIndex, {
          insertAt: elementIndex + 1,
        });

        duplicatedElementPath = elementPaths[0];
      }
    }

    return duplicatedElementPath;
  }

  /**
   * Takes an element returned from getElement and provides the path
   * @param element
   * @return array where each element is part of the path to the specified element
   */
  getElementPath(/** Immutable **/ element, /** optional **/ stateIndex) {
    var path = null;

    if (arguments.length == 2 && stateIndex >= 0) {
      path = this._getStateElementPath(this.getState(stateIndex), stateIndex, element);
    } else {
      var index = this._getElementIndex(this.data.get(ELEMENTS), element);

      if (index != -1) {
        path = [this.id, "elements", index];
      } else if (stateIndex != -1) {
        var states = this.getStates();
        if (states) {
          states.forEach(function (state, idx) {
            path = this._getStateElementPath(state, idx, element);
            return path == null;
          }, this);
        }
      }
    }

    return path;
  }

  _getStateElementPath(state, stateIndex, element) {
    var path = null;

    if (state) {
      var index = this._getElementIndex(state.get(ELEMENTS), element);
      if (index != -1) {
        path = [this.id, STATES, stateIndex, ELEMENTS, index];
      }
    }

    return path;
  }

  _getElementIndex(elements, element) {
    return elements ? elements.indexOf(element) : -1;
  }

  getElements() {
    return this.data.getIn([ELEMENTS], false);
  }

  /**
   * Counts the elements held at an elements collection path. Handles both top level
   * and state element paths.
   * @param {Array<string> | string} elementsPath - path to an elements collection, block id included
   * @returns {number | undefined}
   */
  getElementCountAtPath(elementsPath) {
    return this.data.getIn(IDUtils.getArray(elementsPath, 1))?.size;
  }

  /**
   * Gets a block's element using a state index or element path array
   * @param {number | Array<string>} elementIndexOrPath
   * @param {number=} stateIndex
   * @returns {ElementInfo | null}
   */
  getElement(elementIndexOrPath, stateIndex) {
    let element = null;

    if (Array.isArray(elementIndexOrPath)) {
      const blockId = this.data?.get("id");
      const removeBlockId = blockId && blockId === elementIndexOrPath[0];

      element = this.data.getIn(elementIndexOrPath.slice(Number(removeBlockId)));
    } else {
      if (!_.isNil(stateIndex)) {
        element = this.data.getIn([STATES, stateIndex, ELEMENTS, elementIndexOrPath]);
      } else {
        element = this.data.getIn([ELEMENTS, elementIndexOrPath]);
      }
    }

    return element;
  }

  /**
   * Returns an info object containing block position info
   * @param {Array<string>} elementPath
   * @return object with element type, blockId, blockIndex, stateId, index
   */
  getElementInfo(elementPath) {
    let info = null;
    const element = fromJS(this.getElement(elementPath));

    if (element) {
      let index = parseInt(_.last(elementPath)); //index of element within its parent container
      let blockIndex = index; //index of the element within the block.  This is different for elements in states.
      let stateId = -1; //means not in a state
      let parentPath = _.slice(elementPath, 0, elementPath.length - 2); //trim off 'elements' and index

      // this check might need to be state specific
      if (parentPath.length == 3) {
        stateId = parseInt(parentPath[2]);

        let statesIndex = this.getStateElementIndex();
        if (statesIndex >= 0) {
          blockIndex = statesIndex;
        }
      }

      const type = element.get("type");
      const contained = !!element.get(PARENT_ID);

      info = {
        type,
        blockId: this.id,
        blockIndex: blockIndex,
        index: index,
        lock: element.get(AppConstants.LOCK_EDITING_KEY) || false,
        stateId: stateId,
        contained,
      };

      if (contained) {
        info.containedBy = parentPath.join(".");
        const container = this.getElementContainerEntity(parentPath);
        info.containerId = container.data.get("id");
      }
    }

    return info;
  }

  updateElementName(/** Array **/ elementPath, name) {
    const containerPath = this._getContainerPath(elementPath);
    let element = this.getElement(elementPath);

    if (element) {
      const elementId = parseInt(_.last(elementPath));
      const elementIdIsIndex = !Number.isNaN(elementId);

      const index = elementIdIsIndex ? parseInt(_.last(elementPath)) : element.get("id");
      const elements = this.data.getIn(containerPath);

      if (_.isEmpty(name)) {
        element = element.delete("name");
        element = element.delete("slug");
      } else {
        var nameAndSlug = NameSlugFormatter.format(name, elements, element);
        element = element.merge(nameAndSlug);
      }

      containerPath.push(index); //set index on container path so element can be set in place

      this.data = this.data.setIn(containerPath, element);
    }

    return element;
  }

  updateStateName(stateIndex, name) {
    var state = this.getState(stateIndex);
    var states = this.getStates();

    if (state && states) {
      var nameAndSlug = NameSlugFormatter.format(name, states, state);

      this.data = this.data.mergeIn([STATES, stateIndex], nameAndSlug);
    }

    return state;
  }

  /**
   * Delete by key and also clean up if the parent object is empty
   * @param {Immutable.Map} block - The block to modify
   * @param {Array} key - The key path to delete
   */
  _deleteInAndPruneEmpty(block, key) {
    block.deleteIn(key);
    if (key.length > 2) {// based on key do we have a parent object?
      const parentKey = key.slice(0, -1);
      const parentObj = block.getIn(parentKey);
      if (Immutable.Map.isMap(parentObj) && parentObj.isEmpty()) {
        block.deleteIn(parentKey);
      }
    }
  }

  applyUpdate(/** vo/BlockUpdate **/ blockUpdate) {
    var updates = blockUpdate.updates;
    var origBlock = this.data;

    var updatedBlock = origBlock.withMutations((block) => {
      updates.forEach((update) => {
        if (update.action) {
          if (AppConstants.UPDATE_ACTION_SET == update.action) {
            var value = update.value;
            //make sure to convert to Immutable object before setting
            if (_.isObject(update.value)) {
              value = Immutable.fromJS(value);
            }
            block.setIn(update.key, value);
          } else if (AppConstants.UPDATE_ACTION_DELETE == update.action) {
            this._deleteInAndPruneEmpty(block, update.key);
          } else if (AppConstants.UPDATE_ACTION_MERGE == update.action) {
            block.mergeIn(update.key, update.value);
          }
        } else {
          //default action handling
          if (_.isObject(update.value)) {
            block.mergeIn(update.key, update.value);
          } else {
            if (_.isEmpty(update.value) && !_.isNumber(update.value)) {
              this._deleteInAndPruneEmpty(block, update.key);
            } else {
              block.setIn(update.key, update.value);
            }
          }
        }
      });

      return block;
    });

    this.data = updatedBlock;
  }

  /**
   * Removes element and possibly block id, resulting in a path containing elements.
   * @param {Array<string>} elementPath
   * @returns {Array<string>}
   */
  _getContainerPath(elementPath) {
    //take block id off
    const hasBlockId = [3, 5, 7].includes(elementPath.length);
    //take id off of path
    return [...elementPath].slice(Number(hasBlockId), -1);
  }

  _createStatesElement() {
    var statesElement = ModelFactory.getStatesElement();
    statesElement = Immutable.fromJS(statesElement);

    var elements = this.getElements();
    elements = elements.push(statesElement);

    this.data = this.data.set(ELEMENTS, elements);
  }

  _removeStatesElement() {
    var statesIndex = this.getStateElementIndex();
    if (statesIndex >= 0) {
      this.data = this.data.set(ELEMENTS, this.data.get(ELEMENTS).delete(statesIndex));
    }
  }

  _generateStatePath(index) {
    return [this.id, STATES, index];
  }

  _getContainerAndPath(stateIndex) {
    let container = null;
    let path = [ELEMENTS];
    if (!_.isNil(stateIndex) && stateIndex !== -1) {
      let state = this.getState(stateIndex);
      if (state) {
        container = state.get(ELEMENTS);
        path.unshift(stateIndex);
        path.unshift(STATES);
      }
    } else {
      container = this.data.get(ELEMENTS);
    }
    return { container, path };
  }

  isSiteBlock() {
    return this.data.get("pType", "page") == AppConstants.SITE_ITEM_SITE;
  }

  applyToParent() {
    if (this.parent) {
      this.parent.setBlock(this);
    }
  }

  /**
   * Gets an element container entity from a given path.
   * @param {Array<string> | string} path
   * @returns {ElementContainer}
   */
  getElementContainerEntity(path) {
    if (!path) return null;

    let pathArray = Array.isArray(path) ? path : path.split(".");

    // try searching for the container
    if (pathArray.length === 1) {
      const probablyID = pathArray[0];
      pathArray = this.findElementContainerEntityById(probablyID);
    }

    if (!pathArray) return null;

    const innerPath = pathArray[0] === this.id ? pathArray.slice(1) : pathArray;
    const data = this.data.getIn(innerPath);

    return data && data.get("type") === DataTypes.GROUP
      ? new ElementContainer(this.data.getIn(innerPath), this, [this.id, ...innerPath])
      : null;
  }

  /**
   * Gets containing entity if it exists.
   * Removes the extra step of grabbing the container path and then container from an element path.
   * @param {Array<string> | string} containedPath
   * @returns {ElementContainer}
   */
  getContainingParentEntity(containedPath) {
    const pathArray = Array.isArray(containedPath) ? containedPath : containedPath.split(".");
    const containerPath = IDUtils.getElementContainerFromPath(pathArray);
    return containerPath ? this.getElementContainerEntity(containerPath) : null;
  }

  /**
   * Builds a path to an element container. Uses for..of loops to bail early.
   * @param {string} containerId - The container id as it is stored on the container element. Not a path.
   * @returns {Array<string> | null}
   */
  findElementContainerEntityById(containerId) {
    let elementIndex = 0;
    let stateIndex = 0;

    for (const element of this.getElements()) {
      if (element.get("id") === containerId) return [this.id, ELEMENTS, elementIndex];
      elementIndex += 1;
    }

    for (const state of this.getStates()) {
      elementIndex = 0;
      for (const element of state.get("elements")) {
        if (element.get("id") === containerId)
          return [this.id, STATES, stateIndex, ELEMENTS, elementIndex];
        elementIndex += 1;
      }
      stateIndex += 1;
    }

    return null;
  }

  /**
   *
   * @param {Array<string> | string} path
   * @returns {number| undefined}
   */
  getStateIndexFromPath(path) {
    const splitPath = IDUtils.getArray(path);
    const stateFlag = splitPath.indexOf(STATES);
    return stateFlag === -1 ? undefined : Number(splitPath[stateFlag + 1]);
  }

  get id() {
    return this.data.get("id");
  }

  get slug() {
    return this.data.get("slug");
  }

  get name() {
    return this.data.get("name");
  }
}

export default Block;
